import { createRouter, createWebHistory } from "vue-router";
import { keycloak } from "../main.ts";
import { isTextManagementAvailable } from "../utils/environmentUtils.ts";
import { decideNavigation } from "./navigationRules.ts";

// Import components/views to use as routes
import Home from "../views/HomePage.vue";
import SectionOverview from "../views/SectionOverviewView.vue";
import Participants from "../views/ParticipantView.vue";
import Trainers from "../views/TrainerView.vue";
import Projects from "../views/TrainerProjects.vue";
import BillingView from "../views/BillingView.vue";
import GenerationStatisticsView from "../views/GenerationStatisticsView.vue";
import LanguageActivation from "../views/LanguageActivation.vue";
import ProfileGeneration from "../views/ProfileGenerationView.vue";
import DevCockpit from "../views/DevCockpitView.vue";
import ReleaseNotes from "../views/ReleaseNotesView.vue";
import BannerConfiguration from "../views/BannerConfiguration.vue";
import StartPageBannerConfiguration from "../views/StartPageBannerConfiguration.vue";
import TextDiffs from "../views/TextDiffs.vue";
import TextMaintenance from "../views/TextMaintenance.vue";
import TextSearch from "../views/TextSearch.vue";

// Base routes that are always available
const baseRoutes = [
    {
        path: "/",
        name: "Home",
        component: Home,
        meta: {
            requiresAuth: false,
        },
    },
    // Per-team overview pages (Home links here); one component keyed by meta.sectionKey.
    {
        path: "/support",
        name: "SupportOverview",
        component: SectionOverview,
        meta: { requiresAuth: true, sectionKey: "support" },
    },
    {
        path: "/pe",
        name: "PeOverview",
        component: SectionOverview,
        meta: { requiresAuth: true, sectionKey: "pe" },
    },
    {
        path: "/marketing",
        name: "MarketingOverview",
        component: SectionOverview,
        meta: { requiresAuth: true, sectionKey: "marketing" },
    },
    {
        path: "/statistics",
        name: "StatisticsOverview",
        component: SectionOverview,
        meta: { requiresAuth: true, sectionKey: "statistics" },
    },
    {
        path: "/dev",
        name: "DevOverview",
        component: SectionOverview,
        meta: { requiresAuth: true, sectionKey: "dev" },
    },
    {
        path: "/participants",
        name: "Participants",
        component: Participants,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/trainers",
        name: "Trainers",
        component: Trainers,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/projects",
        name: "Projects",
        component: Projects,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/billing",
        name: "Billing",
        component: BillingView,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/statistics/profiles",
        name: "GenerationStatistics",
        component: GenerationStatisticsView,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/language-activation",
        name: "LanguageActivation",
        component: LanguageActivation,
        meta: {
            requiresAuth: true,
        },
    },
    {
        // Profile-generation monitor — shared by the Support and Dev sections.
        path: "/profile-generation",
        name: "ProfileGeneration",
        component: ProfileGeneration,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/dev/cockpit",
        name: "DevCockpit",
        component: DevCockpit,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: "/dev/release-notes",
        name: "ReleaseNotes",
        component: ReleaseNotes,
        meta: {
            requiresAuth: true,
        },
    },
    {
        // A base route, NOT behind isTextManagementAvailable(): banners are maintained per
        // environment, so production banners are maintained in the production admin (RDT-339).
        path: "/banners",
        name: "BannerConfiguration",
        component: BannerConfiguration,
        meta: {
            requiresAuth: true,
        },
    },
    {
        // A base route for the same reason as /banners: marketing schedules a campaign in the
        // admin of the environment it should run in (RDT-359).
        path: "/start-page-banners",
        name: "StartPageBannerConfiguration",
        component: StartPageBannerConfiguration,
        meta: {
            requiresAuth: true,
        },
    },
];

// Text management routes (only available in non-production environments)
const textManagementRoutes: any[] = [];

if (isTextManagementAvailable()) {
    textManagementRoutes.push(
        {
            path: "/text-maintenance",
            name: "TextMaintenance",
            component: TextMaintenance,
            meta: {
                requiresAuth: true,
            },
        },
        {
            path: "/text-diffs",
            name: "TextDiffs",
            component: TextDiffs,
            meta: {
                requiresAuth: true,
            },
        },
        {
            path: "/text-search",
            name: "TextSearch",
            component: TextSearch,
            meta: {
                requiresAuth: true,
            },
        },
        {
            path: "/text-dependencies",
            name: "TextDependencies",
            component: () => import("../views/TextDependencies.vue"),
            meta: {
                requiresAuth: true,
            },
        },
    );
}

// Combine all routes
const routes = [...baseRoutes, ...textManagementRoutes];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

export default router;

router.beforeEach((to, _, next) => {
    const decision = decideNavigation({
        routeName: typeof to.name === "string" ? to.name : null,
        requiresAuth: Boolean(to.meta.requiresAuth),
        textManagementAvailable: isTextManagementAvailable(),
        authenticated: Boolean(keycloak.authenticated),
        isAdmin: Boolean(keycloak.hasRealmRole("PERSOLOG_ONLINE_ADMIN")),
    });

    switch (decision.type) {
        case "home":
            next({ name: "Home" });
            return;
        case "login":
            keycloak.login({ redirectUri: globalThis.location.href });
            return;
        default:
            next();
    }
});

router.afterEach(() => {
    if (globalThis.location.hash) {
        const url = new URL(globalThis.location.href);
        url.hash = '';
        globalThis.history.pushState({}, "", url);
    }
});
